import * as Sentry from "@sentry/browser";
import { isDev, isStorybook } from "shared/constants/Environment";
import { reportClientError } from "shared/services/clientErrorSink";

const dsn = import.meta.env.VITE_SENTRY_DSN;

if (!isDev && !isStorybook) {
  Sentry.init({
    dsn,
    environment: import.meta.env.VITE_ENVIRONMENT,
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration(),
    ],
    tracesSampleRate: 1.0,
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,
    tracePropagationTargets: ["localhost"],
  });
}

/**
 * Report an error somewhere a person will see it.
 *
 * Three destinations, and only the third is load-bearing today. The console is
 * for whoever has the tab open. `reportClientError` is the one that reaches
 * us: it writes `client_errors/{id}`, which a trigger republishes to Cloud
 * Error Reporting (PRD J5, ADR 0019).
 *
 * `Sentry.captureException` is a no-op and has always been one —
 * `VITE_SENTRY_DSN` is empty in every environment file, so `Sentry.init` above
 * has never run with a DSN. The call is kept because it costs nothing and the
 * ErrorBoundary in App.tsx is Sentry's either way. ⚠️ **Before you fill that
 * DSN in:** the init above also enables `replayIntegration` at a 10% session
 * sample and 100% on error, so a DSN switches on session recording of an app
 * that shows colleagues names, addresses and what they asked for. ADR 0019 is
 * the decision that we report to Cloud Error Reporting instead; turning Sentry
 * on is a privacy question, not a config change.
 *
 * Deliberately not awaited. Callers are `catch` blocks that have their own
 * work to finish, and the sink already guarantees it neither throws nor
 * rejects — so there is nothing to wait for and nothing to handle.
 */
export const logError: typeof Sentry.captureException = (
  exception,
  captureContext,
) => {
  // eslint-disable-next-line no-console
  console.error(exception);
  void reportClientError(exception);
  return Sentry.captureException(exception, captureContext);
};

export const logWarning: typeof Sentry.captureMessage = (
  message,
  captureContext,
) => {
  console.warn(message);
  return Sentry.captureMessage(message, captureContext);
};

export const logMessage: typeof Sentry.captureMessage = (
  message,
  captureContext,
) => {
  console.info(message);
  return Sentry.captureMessage(message, captureContext);
};
